import { useCallback, useEffect, useRef, useState } from 'react'
import { BrandLoader } from '@/components/BrandLoader'
import { Link } from 'react-router-dom'
import { AlertTriangle } from 'lucide-react'
import { exchangeOAuthCodeDirect } from '@/lib/authDirect'
import { exchangeFailureText, readOAuthCallback } from '@/lib/oauthCallback'
import { reportError } from '@/lib/errorReporter'

// /auth/callback — where Google or Microsoft hands the user back
// (startOAuthDirect in src/lib/authDirect.ts). Pre-auth: swap the code for a
// session ONCE and reload onto the app, or say in our own words why not.

export default function AuthCallbackPage() {
  // Read once, at mount. The URL is attacker-shaped; readOAuthCallback never
  // lets its prose through.
  const [callback] = useState(() => readOAuthCallback(window.location.search, window.location.hash))
  const [error, setError]       = useState<string | null>(callback.kind === 'error' ? callback.message : null)
  const [canRetry, setCanRetry] = useState(false)
  const started = useRef(false)

  const finish = useCallback(async (code: string) => {
    setError(null)
    setCanRetry(false)
    const result = await exchangeOAuthCodeDirect(code)
    if (result.ok) {
      // replace, not reload: the spent code must not stay in the address bar
      // or the back stack. The app boots signed in on '/'.
      window.location.replace('/')
      return
    }
    // A refused code with a verifier present is ours to look at (a clock-skewed
    // flow state, a misconfigured provider); a dropped connection is not.
    if (result.reason === 'rejected') {
      void reportError(new Error(`oauth code exchange rejected: ${result.error.slice(0, 200)}`),
        { context: { component: 'auth-callback' } })
    }
    setError(exchangeFailureText(result.reason))
    setCanRetry(result.reason === 'network')
  }, [])

  useEffect(() => {
    // StrictMode runs effects twice in dev, and the code is single-use.
    if (started.current || callback.kind !== 'code') return
    started.current = true
    void finish(callback.code)
  }, [callback, finish])

  if (!error) {
    return (
      <div className="min-h-full flex items-center justify-center p-6 surface-sunk">
        <div className="w-full max-w-sm card p-6 text-center space-y-3" role="status">
          <BrandLoader className="w-10 h-10 mx-auto" label={null} />
          <h1 className="text-lg font-semibold text-fg">Signing you in…</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full flex items-center justify-center p-6 surface-sunk">
      <div className="w-full max-w-sm card p-6 text-center space-y-3">
        <AlertTriangle size={32} className="text-amber-400 mx-auto" />
        <h1 className="text-lg font-semibold text-fg">Sign-in didn't finish</h1>
        <p className="text-sm text-fg-muted">{error}</p>
        <div className="flex flex-col gap-2 pt-1">
          {canRetry && callback.kind === 'code' && (
            <button type="button" className="btn-primary w-full" onClick={() => void finish(callback.code)}>
              Try again
            </button>
          )}
          <Link to="/login" replace className="btn-secondary w-full">Back to sign-in</Link>
        </div>
      </div>
    </div>
  )
}
